import Box from '@mui/material/Box';
import MainTable from '@components/MainTable';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import {
  BillingHistory,
  BillingHistoryResponse,
} from '@app/types/payment/billing';
import billingHistoryTableColumns from '@features/settings-billing/components/BillingHistoryTable/columns';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import useToast from '@hooks/useToast';
import { urls } from '@api/urls.ts';
import { AlaCarteApi } from '@api/alacarte/alaCarteApi';
import { useEffect } from 'react';
import { TableMetaCustom } from '@app/types/overrides/table-meta';
import {
  billingDocumentFor,
  buildBillingRows,
  PurchaseRecord,
} from '@features/settings-billing/model/billing-rows';

function BillingHistoryTable() {
  const { showError } = useToast();
  const { data, isLoading } = useDataFetcher<BillingHistoryResponse>({
    url: urls.caseSubscription.invoices,
  });
  const { data: purchasesData, isLoading: isPurchasesLoading } = useDataFetcher<
    PurchaseRecord[]
  >({
    url: urls.alaCarte.purchases,
  });

  const { table, setData } = useTanStackTable<BillingHistory>({
    tableData: [],
    columnConfig: billingHistoryTableColumns,
    options: {
      meta: {
        // Open what Stripe issued: the invoice for a subscription charge, the
        // receipt for a one-time purchase (ZLAYT-1476). For a receipt the tab
        // is opened before the lookup, so the browser does not block it.
        handleDownloadRow: async (row: BillingHistory) => {
          const doc = billingDocumentFor(row);
          if (!doc) return;
          if (doc.kind === 'invoice') {
            window.open(doc.url, '_blank', 'noopener,noreferrer');
            return;
          }
          const tab = window.open('', '_blank');
          if (tab) tab.opener = null;
          try {
            const url = await AlaCarteApi.getPurchaseReceiptUrl(doc.purchaseId);
            if (tab) tab.location.href = url;
            else window.location.assign(url);
          } catch (e) {
            tab?.close();
            showError(
              'Receipt not available',
              'Stripe has no receipt for this purchase yet. Please try again later or contact support.'
            );
          }
        },
      } as TableMetaCustom<BillingHistory>,
    },
  });

  useEffect(() => {
    setData(
      buildBillingRows(
        data?.invoices_history || [],
        Array.isArray(purchasesData) ? purchasesData : []
      )
    );
  }, [data, purchasesData, setData]);

  return (
    <Box sx={{ overflowX: 'auto', flexGrow: 1 }}>
      <MainTable
        table={table}
        isLoading={isLoading || isPurchasesLoading}
        noDataDescription="No billing history available yet."
      />
      <MainTablePagination
        table={table}
        isLoading={isLoading || isPurchasesLoading}
      />
    </Box>
  );
}

export default BillingHistoryTable;
